<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import { ShieldCheck, BadgeCheck, Search, ChevronRight } from "lucide-vue-next";
import { useI18n } from "vue-i18n";

import { getNotaryListForSelection } from "@/entities/session";

import { UserAvatar } from "@/shared/ui/user-avatar";
import { useConfirm } from "@/shared/ui/alert-dialog";
import BottomSheet from "@/shared/ui/bottom-sheet/BottomSheet.vue";
import Button from "@/shared/ui/button/Button.vue";
import { Badge } from "@/shared/ui/badge";
import { Empty } from "@/shared/ui/empty";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import NotaryListPanel from "../components/NotaryListPanel.vue";
import InscriptionSignBlock from "../components/InscriptionSignBlock.vue";
import { CATEGORY_CODES } from "@/shared/lib/constants";
import { REGION_LABELS } from "@/shared/lib/regionLabels";

// Города республиканского значения — всегда первыми в превью
const PRIORITY_CITY_CODES = ["01", "02", "17"];

const sortByCodeAsc = (a, b) =>
  a.code.localeCompare(b.code, undefined, { numeric: true });

const sortCitiesFirst = (a, b) => {
  const ai = PRIORITY_CITY_CODES.indexOf(a.code);
  const bi = PRIORITY_CITY_CODES.indexOf(b.code);
  if (ai !== -1 && bi !== -1) return ai - bi;
  if (ai !== -1) return -1;
  if (bi !== -1) return 1;
  return sortByCodeAsc(a, b);
};

const regionEntries = Object.entries(REGION_LABELS)
  .map(([code, name]) => ({ code: String(code), name }))
  .sort(sortByCodeAsc);
const regionsPreview = [...regionEntries].sort(sortCitiesFirst).slice(0, 3);
const regionsExtraCount = Math.max(
  0,
  regionEntries.length - regionsPreview.length
);

const regionsDialogOpen = ref(false);

function openRegionsDialog() {
  regionsDialogOpen.value = true;
}

const { t } = useI18n();
const confirm = useConfirm();

const props = defineProps({
  form: { type: Object, default: () => ({}) },
  uuid: { type: String, default: null },
  editable: { type: Boolean, default: false },
  assigning: { type: Boolean, default: false },
  assigned: { type: Boolean, default: false },
  hasRecoverer: { type: Boolean, default: false },
});

const emit = defineEmits(["select-notary", "assign-random-notary", "signed"]);

function onAssignRandom() {
  if (!props.editable) return;
  if (props.assigning || props.assigned) return;
  if (!props.hasRecoverer) return;
  emit("assign-random-notary");
}

const loading = ref(false);
const notaries = ref([]);
const total = ref(0);
const search = ref("");

const DEFAULT_NOTARY_IDENTIFIER = "000000000000";

const isP2 = computed(() => props.form?.exec_inscription_type === "P2");

const assignedNotary = computed(() => {
  const id = props.form?.notary_identifier;
  if (!id || id === DEFAULT_NOTARY_IDENTIFIER) return null;
  return props.form?.notary_info || null;
});

const cardNotary = computed(() => assignedNotary.value || null);

const totalCount = computed(() => total.value || notaries.value.length);
const onlineCount = computed(
  () => notaries.value.filter((n) => n.is_online).length
);

const focusedIndex = ref(-1);
const listRef = ref(null);

const isMobile = ref(false);
const sheetOpen = ref(false);
let mq = null;

function applyMq(e) {
  isMobile.value = e.matches;
  if (!isMobile.value) sheetOpen.value = false;
}

let searchTimer = null;

function buildSearchParams(q) {
  const params = {
    page: 1,
    page_size: 50,
    notarial_actions_codes: [CATEGORY_CODES.SOVER_EXECUT_NADPIS],
  };
  if (!q) return params;

  if (/^\d+$/.test(q)) {
    params.identifier = q;
    return params;
  }

  const parts = q.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    params.last_name = parts[0];
  } else if (parts.length === 2) {
    params.last_name = parts[0];
    params.first_name = parts[1];
  } else {
    params.last_name = parts[0];
    params.first_name = parts[1];
    params.middle_name = parts.slice(2).join(" ");
  }
  return params;
}

async function fetchList() {
  loading.value = true;
  try {
    const q = (search.value || "").trim();
    const params = buildSearchParams(q);

    const data = await getNotaryListForSelection(params);
    notaries.value = data?.items || [];
    total.value = data?.total || 0;
    focusedIndex.value = -1;
  } finally {
    loading.value = false;
  }
}

function scrollFocusedIntoView() {
  nextTick(() => {
    if (focusedIndex.value < 0 || !listRef.value) return;
    const el = listRef.value.querySelector(
      `[data-row-index="${focusedIndex.value}"]`
    );
    el?.scrollIntoView({ block: "nearest" });
  });
}

function isNavActive() {
  return (
    !isP2.value &&
    !props.assigned &&
    !props.assigning &&
    notaries.value.length > 0
  );
}

function onKeydown(e) {
  if (!isNavActive()) return;
  const tag = e.target?.tagName;
  if (tag === "TEXTAREA") return;

  const last = notaries.value.length - 1;

  if (e.key === "ArrowDown") {
    e.preventDefault();
    focusedIndex.value =
      focusedIndex.value < 0
        ? 0
        : (focusedIndex.value + 1) % notaries.value.length;
    scrollFocusedIntoView();
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    focusedIndex.value =
      focusedIndex.value <= 0 ? last : focusedIndex.value - 1;
    scrollFocusedIntoView();
  } else if (e.key === "Home") {
    e.preventDefault();
    focusedIndex.value = 0;
    scrollFocusedIntoView();
  } else if (e.key === "End") {
    e.preventDefault();
    focusedIndex.value = last;
    scrollFocusedIntoView();
  } else if (e.key === "Enter") {
    if (focusedIndex.value < 0) return;
    e.preventDefault();
    const n = notaries.value[focusedIndex.value];
    if (n) selectNotary(n);
  }
}

function buildFullName(n) {
  if (!n) return "";
  return [n.last_name, n.first_name, n.middle_name].filter(Boolean).join(" ");
}

async function selectNotary(notary) {
  if (!props.editable) return;
  if (props.assigning || props.assigned) return;
  if (!notary) return;

  const fio = buildFullName(notary) || notary.identifier;
  const message =
    t("Назначить нотариуса") +
    ` ${fio}? ` +
    t("Изменить выбор после подтверждения нельзя.");
  try {
    await confirm(message, t("Подтверждение"), {
      confirmButtonText: t("Назначить"),
      cancelButtonText: t("Отмена"),
    });
  } catch {
    return;
  }

  sheetOpen.value = false;
  emit("select-notary", notary);
}

function onSigned(payload) {
  emit("signed", payload);
}

watch(search, () => {
  clearTimeout(searchTimer);
  focusedIndex.value = -1;
  searchTimer = setTimeout(fetchList, 400);
});

watch(
  () => props.assigned,
  (val) => {
    if (val) clearTimeout(searchTimer);
  }
);

onMounted(() => {
  if (!isP2.value && !props.assigned) {
    fetchList();
  }
  window.addEventListener("keydown", onKeydown);
  mq = window.matchMedia("(max-width: 768px)");
  applyMq(mq);
  mq.addEventListener("change", applyMq);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  if (mq) mq.removeEventListener("change", applyMq);
  clearTimeout(searchTimer);
});
</script>

<template>
  <!-- ===== P2 (барабан) ===== -->
  <div v-if="isP2 && !assigned" class="notary-tab notary-tab--p2">
    <div class="p2-placeholder">
      <div class="p2-icon-wrap">
        <BadgeCheck class="p2-icon" />
      </div>
      <h3 class="p2-title">
        {{ $t("Нотариус будет назначен автоматически") }}
      </h3>
      <p class="p2-text">
        {{
          $t(
            "Для выбранного вида исполнительной надписи (P2) применяется метод «барабан» — заявление автоматически распределяется случайному онлайн-нотариусу по территории должника."
          )
        }}
      </p>
      <p v-if="!hasRecoverer" class="p2-hint">
        {{
          $t(
            "Для назначения исполнителя сначала добавьте участника-исполнителя (RECOVERER)."
          )
        }}
      </p>
      <div v-if="editable" class="p2-action">
        <Button
          variant="default"
          :disabled="!hasRecoverer || assigning || assigned"
          @click="onAssignRandom"
        >
          {{ $t("Назначить нотариуса") }}
        </Button>
      </div>
    </div>
  </div>

  <!-- ===== Назначен: горизонтальная карточка + sign-block ===== -->
  <div
    v-else-if="assigned && cardNotary"
    class="notary-tab notary-tab--assigned"
  >
    <div class="notary-summary">
      <UserAvatar
        :name="buildFullName(cardNotary)"
        :identifier="cardNotary.identifier"
        :seed="cardNotary.uuid"
        :status="cardNotary.is_online ? 'active' : 'offline'"
        :size="44"
      />
      <div class="summary-main">
        <div class="summary-name">
          <div class="summary-fio">
            {{ buildFullName(cardNotary) || cardNotary.identifier }}
          </div>
          <div class="verified">
            <ShieldCheck class="w-[13px] h-[13px]" />
            {{ $t("Верифицирован") }}
          </div>
        </div>
        <div class="summary-role">
          <div class="role-pill">{{ $t("Нотариус") }}</div>
          <div v-if="cardNotary.identifier">· {{ cardNotary.identifier }}</div>
          <div v-if="cardNotary.email">· {{ cardNotary.email }}</div>
        </div>
      </div>
    </div>

    <InscriptionSignBlock
      :uuid="uuid"
      :form="form"
      :editable="editable"
      @signed="onSigned"
    />
  </div>

  <!-- ===== Не назначен: список + stat-row + empty ===== -->
  <div v-else class="notary-tab">
    <!-- Mobile trigger вместо левой колонки -->
    <button
      v-if="isMobile"
      type="button"
      class="mobile-trigger"
      @click="sheetOpen = true"
    >
      <div class="mobile-trigger-icon">
        <Search class="w-4 h-4" />
      </div>
      <div class="mobile-trigger-main">
        <div class="mobile-trigger-label">{{ $t("Шаг 2") }}</div>
        <div class="mobile-trigger-value">{{ $t("Выбрать нотариуса") }}</div>
      </div>
      <ChevronRight class="w-4 h-4 text-muted-foreground" />
    </button>

    <!-- Desktop left-pane -->
    <div v-else class="left-pane">
      <NotaryListPanel
        ref="listRef"
        v-model:search="search"
        v-model:focused-index="focusedIndex"
        :notaries="notaries"
        :loading="loading"
        :assigning="assigning"
        :total-count="totalCount"
        @select="selectNotary"
      />
    </div>

    <!-- Right-pane: stat-row + empty -->
    <div class="right-pane">
      <div class="stat-row">
        <div class="stat">
          <div class="stat-label">{{ $t("Доступны сейчас") }}</div>
          <div class="stat-value">
            {{ onlineCount }}
            <div class="stat-delta">{{ $t("из") }} {{ totalCount }}</div>
          </div>
          <div class="stat-sub">
            {{ $t("Подпишут заявление за 5–15 минут") }}
          </div>
        </div>
        <div class="stat">
          <div class="stat-label">{{ $t("Регионов") }}</div>
          <div class="stat-value">{{ regionEntries.length }}</div>
          <div class="stat-sub stat-regions">
            <template v-for="(region, i) in regionsPreview" :key="region.code">
              <div v-if="i > 0" class="region-dot">·</div>
              <div class="stat-sub">{{ region.name }}</div>
            </template>
            <button
              v-if="regionsExtraCount > 0"
              type="button"
              class="region-more"
              @click="openRegionsDialog"
            >
              <Badge variant="outline" class="stat-sub text-[9px]">
                <div class="text-[9px]">+{{ regionsExtraCount }}</div>
              </Badge>
            </button>
          </div>
        </div>
        <div class="stat">
          <div class="stat-label">{{ $t("Среднее время") }}</div>
          <div class="stat-value">
            ~ 8
            <div class="stat-unit">{{ $t("мин") }}</div>
          </div>
          <div class="stat-sub">{{ $t("От выбора до подписи") }}</div>
        </div>
      </div>

      <Empty
        card
        :title="$t('Выберите нотариуса')"
        :description="
          $t(
            'После выбора нотариуса его данные автоматически подставятся в редактор заявления, и вы сможете подписать его.'
          )
        "
      >
        <template #icon>
          <ShieldCheck />
        </template>
      </Empty>
    </div>

    <!-- Mobile sheet -->
    <BottomSheet
      v-if="isMobile"
      v-model="sheetOpen"
      :title="$t('Выберите нотариуса')"
      height="auto"
      class="notary-sheet"
    >
      <NotaryListPanel
        v-model:search="search"
        v-model:focused-index="focusedIndex"
        :notaries="notaries"
        :loading="loading"
        :assigning="assigning"
        :total-count="totalCount"
        :show-footer="false"
        bare
        @select="selectNotary"
      />
    </BottomSheet>

    <Dialog v-model:open="regionsDialogOpen">
      <DialogContent class="region-dialog">
        <DialogHeader>
          <DialogTitle>{{ $t("Регионы") }}</DialogTitle>
        </DialogHeader>
        <ul class="region-list">
          <li
            v-for="region in regionEntries"
            :key="region.code"
            class="region-row"
          >
            <Badge variant="outline" class="region-code">{{
              region.code
            }}</Badge>
            <div class="region-name">{{ region.name }}</div>
          </li>
        </ul>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
.notary-tab {
  @apply w-full h-full flex gap-6;
}

.left-pane {
  @apply w-[380px] flex-shrink-0 flex flex-col gap-3 min-h-0;
}

.right-pane {
  @apply flex-1 flex flex-col gap-4 min-w-0;
}

/* Bottom-sheet overrides for notary list */
.notary-sheet :deep(.sheet) {
  max-height: 92vh;
}

.notary-sheet :deep(.sheet-body) {
  @apply px-3 py-2;
}

.notary-sheet :deep(.sheet-header) {
  @apply px-4 py-3;
}

/* Mobile trigger */
.mobile-trigger {
  @apply flex items-center gap-3 p-3 w-full bg-card border border-border rounded-[var(--radius)]
    cursor-pointer transition-colors text-left;

  &:hover {
    @apply bg-muted/40;
  }
}

.mobile-trigger-icon {
  @apply w-9 h-9 rounded-[var(--radius)] bg-muted/40 border border-border
    flex items-center justify-center text-muted-foreground shrink-0;
}

.mobile-trigger-main {
  @apply flex-1 min-w-0 flex flex-col gap-[2px];
}

.mobile-trigger-label {
  @apply text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground;
}

.mobile-trigger-value {
  @apply text-sm font-medium text-foreground truncate;
}

/* P2 / assigned modes */
.notary-tab--p2 {
  @apply items-center justify-center;
}

.notary-tab--assigned {
  @apply flex-col gap-4;

  @media (min-width: 1024px) {
    max-width: 50%;
  }
}

.p2-placeholder {
  @apply flex flex-col items-center justify-center text-center gap-3 p-8 bg-card max-w-[520px] w-full mx-auto mb-20;
}

.p2-icon-wrap {
  @apply w-14 h-14 bg-muted flex items-center justify-center;
  border-radius: var(--radius);
}

.p2-icon {
  @apply w-7 h-7 text-foreground;
}

.p2-title {
  @apply text-foreground text-base font-semibold m-0;
}

.p2-text {
  @apply text-sm text-muted-foreground leading-5 max-w-md m-0;
}

.p2-hint {
  @apply text-xs text-yellow-700 leading-5 max-w-md m-0;
}

.p2-action {
  @apply mt-3;
}

/* Notary summary (after assign) */
.notary-summary {
  @apply flex items-center gap-[14px] p-[14px_18px] bg-card border border-border rounded-[var(--radius)];
}

.summary-main {
  @apply flex-1 min-w-0;
}

.summary-name {
  @apply text-[15px] font-semibold text-foreground flex items-center gap-2 flex-wrap;
}

.summary-fio {
  @apply text-foreground;
}

.summary-role {
  @apply text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-1;
}

.role-pill {
  @apply text-[10.5px] font-medium text-foreground bg-muted rounded
    px-[7px] py-[2px] uppercase tracking-wide;
}

.verified {
  @apply text-[11.5px] font-medium text-green-600 flex items-center gap-1;
}

/* Stat row */
.stat-row {
  @apply grid grid-cols-3 gap-3;

  @media (max-width: 1500px) {
    @apply grid-cols-2;
  }
}

.stat {
  @apply bg-card border border-border rounded-[var(--radius)] p-[14px_16px] flex flex-col gap-1;
}

.stat-label {
  @apply text-[11px] font-medium text-muted-foreground uppercase tracking-[0.06em];
}

.stat-value {
  @apply text-[22px] font-semibold text-foreground tracking-[-0.02em]
    inline-flex items-baseline gap-[6px];
}

.stat-delta {
  @apply text-[11px] font-medium text-green-600;
}

.stat-unit {
  @apply text-sm text-muted-foreground font-medium;
}

.stat-sub {
  @apply text-[12.5px] text-muted-foreground;
}

.stat-regions {
  @apply flex flex-wrap items-center gap-x-[6px]  mt-1;
}

.region-name {
  @apply text-[11.5px] text-muted-foreground;

  &--more {
    @apply font-medium text-foreground/80 underline-offset-2;
  }
}

.region-dot {
  @apply text-muted-foreground/60 select-none;
}

.region-more {
  @apply inline-flex items-center gap-x-[6px] bg-transparent border-0 p-0 cursor-pointer outline-none;

  &:hover .region-name--more,
  &:focus-visible .region-name--more {
    @apply underline;
  }
}

.region-list {
  @apply flex flex-col gap-2 m-0 p-0 list-none;
}

.region-row {
  @apply flex items-center gap-3;
}

.region-code {
  @apply min-w-[34px] justify-center;
}

.region-name {
  @apply text-sm text-foreground;
}

.empty-features {
  @apply flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mt-3
    text-sm text-muted-foreground;
}

.empty-feature {
  @apply inline-flex items-center gap-1.5;
}

.empty-feature-icon {
  @apply w-4 h-4 text-muted-foreground;
}

.empty-feature-dot {
  @apply text-muted-foreground/60 select-none;
}

/* ===== Responsive ===== */

/* Tablet portrait + small laptop */
@media (max-width: 1023px) {
  .left-pane {
    width: 320px;
  }
}

/* Mobile / phones */
@media (max-width: 768px) {
  .notary-tab {
    @apply flex-col gap-4;
  }

  .notary-summary {
    @apply gap-3 p-3;
  }

  .summary-name {
    @apply text-sm;
  }
}

/* Small phones */
@media (max-width: 480px) {
  .notary-tab {
    @apply gap-3;
  }

  .stat-row {
    @apply grid-cols-1;
  }

  .stat {
    @apply p-3;
  }

  .stat-value {
    @apply text-lg;
  }

  .notary-summary {
    @apply flex-col items-start;
  }

  .summary-name {
    @apply gap-1;
  }

  .p2-placeholder {
    @apply p-6;
  }

  .p2-icon-wrap {
    @apply w-12 h-12;
  }

  .p2-icon {
    @apply w-6 h-6;
  }

  .p2-title {
    @apply text-sm;
  }

  .p2-text {
    @apply text-[12.5px];
  }
}
</style>
